import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

import { Divider, IconButton, InfoTooltip, Tooltip } from '@treezor/design-system';

import {
  authorizationMti,
  cardSubTypes,
  exemptionType3DS,
  ICardTransaction,
} from '@/features/cards/store/models/card.model';
import { PaymentStatusCell } from '@/features/cards/components/CardTransactionsTable/PaymentStatusCell';
import { PaymentModeCell } from '@/features/cards/components/CardTransactionsTable/PaymentModeCell';
import { PaymentAmountDetail } from '@/features/cards/components/CardTransactionsTable/PaymentAmountDetail';
import { PaymentAuthorizationCell } from '@/features/cards/components/CardTransactionsTable/PaymentAuthorizationCell';
import { PaymentCardholderPresenceCell } from '@/features/cards/components/CardTransactionsTable/PaymentCardholderPresenceCell';
import { PaymentPANEntryMethodCell } from '@/features/cards/components/CardTransactionsTable/PaymentPANEntryMethodCell';
import { useCardTransactionDetails } from '@/features/cards/hooks/useCardTransactionDetails';
import { mappingIso3Countries } from '@/utils/constants';
import { CardTransactionDetailsItem } from '@/features/cards/components/CardTransactionsTable/CardTransactionDetailsItem';

interface ICardTransactionDetailsProps {
  isOpen: boolean;
  transaction: ICardTransaction | null;
  onClose: () => void;
  onFilterByPaymentId?: (paymentId: string) => void;
}

export function CardTransactionDetails({
  isOpen,
  onClose,
  onFilterByPaymentId,
  transaction,
}: ICardTransactionDetailsProps) {
  const { t } = useTranslation();
  const { getIssuerDateTime, merchantCountryCode, handleNavigateToWallet, mccData } = useCardTransactionDetails({
    transaction,
  });

  if (!transaction) return null;

  return (
    <div
      className={clsx(
        'fixed right-0 top-0 w-[25rem] h-full',
        'bg-primary shadow-100 rounded-100 flex flex-col z-[201] transition-transform',
        isOpen ? 'translate-x-0' : 'translate-x-full',
      )}
      data-testid="card-transaction-details-sidebar"
    >
      <div className="flex-shrink-0 pt-7">
        <div className="flex justify-between px-7 pb-5">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="typo-body-light-small text-tertiary">
                {t('pages.currentProfiles.cards.transactions.details.header.transactionId')}
              </span>
              <span className="typo-body-regular-small text-secondary">{transaction.cardtransactionId}</span>
            </div>
            <PaymentModeCell transaction={transaction} shouldShowId={false} />
          </div>

          <IconButton
            onClick={onClose}
            aria-label="close"
            icon="close"
            data-testid="close-transaction-details-sidebar"
            className="h-fit"
          />
        </div>

        <div className="flex flex-col gap-3.5 py-5 bg-secondary items-center border-y border-primary">
          <PaymentAmountDetail transaction={transaction} />
          <PaymentStatusCell status={transaction.paymentStatus} />
          <div className="flex items-center gap-2">
            <span className="typo-body-regular-small text-secondary">
              {getIssuerDateTime(transaction.authorizationIssuerTime)}
            </span>
            <InfoTooltip
              tooltipInfo={t('pages.currentProfiles.cards.transactions.table.headers.authorizationIssuerTime')}
              infoClassName="w-[170px]"
            />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto flex flex-col gap-4 px-7 py-6">
        <div className="flex flex-col gap-2">
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.transactionSubType')}
            value={
              cardSubTypes.includes(transaction?.transactionSubType)
                ? [
                    transaction.transactionSubType,
                    t(
                      `pages.currentProfiles.cards.transactions.table.headers.subType.${transaction.transactionSubType}`,
                    ),
                  ].join(': ')
                : '/'
            }
          />

          <div className="flex items-start gap-4">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.table.headers.paymentCountry')}
            </span>
            <span className="typo-body-regular-paragraph text-secondary">
              {transaction?.paymentCountry
                ? mappingIso3Countries[transaction.paymentCountry]
                  ? t(`shared.countries.${mappingIso3Countries[transaction.paymentCountry]}`)
                  : transaction.paymentCountry
                : '/'}
            </span>
          </div>
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-2">
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.cardId')}
            value={transaction.cardId || '/'}
          />
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.publicToken')}
            value={transaction.publicToken || '/'}
          />
          <div className="flex items-start gap-4">
            <span className="typo-body-light-paragraph text-secondary font-light self-center">
              {t('pages.currentProfiles.cards.transactions.table.headers.walletId')}
            </span>
            {transaction.walletId ? (
              <div className="flex items-center gap-1">
                <span
                  className="typo-body-regular-paragraph text-secondary"
                  data-testid="card-transaction-detail-walletId"
                >
                  {transaction.walletId}
                </span>
                <IconButton
                  icon="chevron_right"
                  variant="secondary"
                  onClick={() => handleNavigateToWallet(transaction.userId, transaction.walletId)}
                  testId="card-transaction-detail-redirect-walletId"
                />
              </div>
            ) : (
              <span className="typo-body-regular-paragraph text-secondary">-</span>
            )}
          </div>
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        {transaction.paymentId && (
          <>
            <div className="flex flex-col gap-2">
              <span className="typo-subtitle-subtitle1">
                {t('pages.currentProfiles.cards.transactions.details.paymentLifecycle')}
              </span>
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <span className="typo-body-light-paragraph text-secondary self-center">
                    {t('pages.currentProfiles.cards.transactions.table.headers.paymentId')}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="typo-body-regular-paragraph text-secondary">{transaction.paymentId}</span>
                    <Tooltip
                      contentClassName="min-w-[200px]"
                      messageContent={t('pages.currentProfiles.cards.transactions.details.paymentLifecycleTooltip')}
                    >
                      <IconButton
                        icon="chevron_right"
                        variant="secondary"
                        onClick={() => transaction?.paymentId && onFilterByPaymentId?.(transaction.paymentId)}
                        testId="card-transaction-detail-redirect-payementId"
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
            <Divider thickness="100" direction="horizontal" className="w-full" />
          </>
        )}

        <div className="flex flex-col gap-2">
          <span className="typo-subtitle-subtitle1">
            {t('pages.currentProfiles.cards.transactions.table.headers.merchant')}
          </span>
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.merchantId')}
            value={transaction.merchantId || '/'}
          />
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.acquirerId')}
            value={transaction.acquirerId || '/'}
          />
          <div className="flex items-start gap-4">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.table.headers.merchantName')}
            </span>
            <div className="flex gap-2">
              <span className="typo-body-regular-paragraph text-secondary">
                {transaction.optimizedMerchantName || transaction.merchantName || '/'}
              </span>
              {transaction?.merchantLogo && (
                <img className="w-5 h-5 bg-primary border border-primary" src={transaction?.merchantLogo} />
              )}
            </div>
          </div>
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.details.merchant.address')}
            value={
              [transaction.merchantCity, transaction.merchantCountry, merchantCountryCode].filter(Boolean).join(', ') ||
              '/'
            }
          />
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.mcc')}
            value={[mccData?.code, mccData?.label].filter(Boolean).join(': ') || '/'}
          />
          {(transaction.merchantCategory || transaction.merchantCategoryGroup || transaction.merchantCategoryType) && (
            <>
              <Divider length="12px" thickness="100" />
              <CardTransactionDetailsItem
                label={t('pages.currentProfiles.cards.transactions.table.headers.merchantCategory')}
                value={transaction.merchantCategory || '/'}
              />
              <CardTransactionDetailsItem
                label={t('pages.currentProfiles.cards.transactions.details.merchant.categoryGroup')}
                value={transaction.merchantCategoryGroup || '/'}
              />
              <CardTransactionDetailsItem
                label={t('pages.currentProfiles.cards.transactions.details.merchant.categoryType')}
                value={transaction.merchantCategoryType || '/'}
              />
            </>
          )}
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-2">
          <span className="typo-subtitle-subtitle1">
            {t('pages.currentProfiles.cards.transactions.table.headers.authorization')}
          </span>
          <div className="flex items-start gap-4">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.table.headers.authorizationResponseCode')}
            </span>
            <PaymentAuthorizationCell
              transaction={transaction}
              config={{
                children: {
                  authorizationMti: { isActive: false },
                },
              }}
              shouldTrancate={false}
            />
          </div>
          <div className="flex items-start gap-4">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.table.headers.authorizationMti')}
            </span>
            <span className="typo-body-regular-paragraph text-secondary">
              {authorizationMti[transaction.authorizationMti]
                ? [
                    transaction.authorizationMti,
                    t(
                      `pages.currentProfiles.cards.transactions.table.headers.authorizationMtiStatus.${authorizationMti[transaction.authorizationMti]}`,
                    ),
                  ].join(': ')
                : transaction.authorizationMti || '/'}
            </span>
          </div>
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.authorizationNote')}
            value={transaction.authorizationNote || '/'}
          />
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-2">
          <span className="typo-subtitle-subtitle1">
            {t('pages.currentProfiles.cards.transactions.details.pos.title')}
          </span>
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.details.pos.terminalId')}
            value={transaction.posTerminalId || '/'}
          />
          <div className="flex flex-col items-start gap-2">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.details.pos.cardholderPresence')}
            </span>
            <PaymentCardholderPresenceCell
              posCardholderPresence={transaction.posCardholderPresence}
              shouldTrancate={false}
            />
          </div>
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.details.pos.cardPresence')}
            value={
              transaction.posCardPresence === '0'
                ? t('pages.cores.no')
                : transaction.posCardPresence === '1'
                  ? t('pages.cores.yes')
                  : t('pages.cores.unknown')
            }
          />
          <div className="flex flex-col items-start gap-2">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.table.headers.panEntryMethod')}
            </span>
            <PaymentPANEntryMethodCell entryMethod={transaction.panEntryMethod} shouldTrancate={false} />
          </div>
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-2">
          <span className="typo-subtitle-subtitle1">
            {t('pages.currentProfiles.cards.transactions.table.headers.3DSecure')}
          </span>
          <CardTransactionDetailsItem
            label={t('pages.currentProfiles.cards.transactions.table.headers.is3DS')}
            value={transaction.is3DS ? t('components.molecules.forms.yes') : t('components.molecules.forms.no')}
          />
          <div className="flex flex-col items-start gap-2">
            <span className="typo-body-light-paragraph text-secondary font-light">
              {t('pages.currentProfiles.cards.transactions.table.headers.3dsExemptionType')}
            </span>
            <span className="typo-body-regular-paragraph text-secondary">
              {exemptionType3DS.includes(transaction['3dsExemptionType'])
                ? [
                    transaction['3dsExemptionType'],
                    t(
                      `pages.currentProfiles.cards.transactions.table.headers.3dsExemptionTypes.${transaction['3dsExemptionType']}`,
                    ),
                  ].join(': ')
                : '/'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
